import React from "react";
import { MoreVertical, ShoppingCart, TrendingDown, History, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "@/lib/i18n";

/**
 * HoldingActionMenu — Phase 8.C-2.
 *
 * Surfaces five clear actions per open holding row so the previous
 * delete-icon shortcut can no longer be misused as a sale workflow:
 *
 *   • Add Purchase           → opens HoldingDialog in "add" mode pre-filled
 *   • Record Sale            → opens RecordSaleModal for this ticker
 *   • View Transactions      → opens TransactionHistoryModal scoped to the
 *                              active lifecycle
 *   • Edit Position Metadata → opens HoldingDialog in "edit" mode
 *   • Delete Incorrect Pos.  → opens the (now stronger) delete confirm
 *
 * Each action is a labeled button (no icon-only on mobile) and emits a
 * dedicated callback so the parent decides what dialog to mount. The
 * destructive delete action is visually separated and color-coded so
 * the user can never accidentally fire it.
 */
export default function HoldingActionMenu({
  position,
  onAddPurchase,
  onRecordSale,
  onViewTransactions,
  onEditMetadata,
  onDeleteIncorrect,
  variant = "row", // "row" (desktop) | "mobile" (full-width inline)
}) {
  const { t } = useTranslation();
  const ticker = position?.ticker || "";

  const items = (
    <>
      <DropdownMenuItem
        data-testid={`holding-action-add-purchase-${ticker}`}
        onClick={() => onAddPurchase?.(position)}
      >
        <ShoppingCart className="w-4 h-4 mr-2" />
        {t("portfolio.action_menu.add_purchase")}
      </DropdownMenuItem>
      <DropdownMenuItem
        data-testid={`holding-action-record-sale-${ticker}`}
        onClick={() => onRecordSale?.(position)}
      >
        <TrendingDown className="w-4 h-4 mr-2" />
        {t("portfolio.action_menu.record_sale")}
      </DropdownMenuItem>
      <DropdownMenuItem
        data-testid={`holding-action-view-transactions-${ticker}`}
        onClick={() => onViewTransactions?.(position)}
      >
        <History className="w-4 h-4 mr-2" />
        {t("portfolio.action_menu.view_transactions")}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        data-testid={`holding-action-edit-metadata-${ticker}`}
        onClick={() => onEditMetadata?.(position)}
      >
        <Pencil className="w-4 h-4 mr-2" />
        {t("portfolio.action_menu.edit_metadata")}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        data-testid={`holding-action-delete-incorrect-${ticker}`}
        onClick={() => onDeleteIncorrect?.(position)}
        className="text-[#FF3DA6] focus:text-[#FF3DA6] focus:bg-[#FF3DA6]/10"
      >
        <Trash2 className="w-4 h-4 mr-2" />
        {t("portfolio.action_menu.delete_incorrect")}
      </DropdownMenuItem>
    </>
  );

  if (variant === "mobile") {
    // Mobile gets a full-width labeled "Actions" trigger so the user
    // sees the word, not a tiny dot — per spec ("Avoid tiny icon-only
    // actions without labels on mobile.").
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            data-testid={`portfolio-holding-mobile-actions-${ticker}`}
          >
            <MoreVertical className="w-4 h-4 mr-1.5" />
            {t("portfolio.action_menu.label")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[220px]">{items}</DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("portfolio.action_menu.label")}
          data-testid={`portfolio-holding-actions-${ticker}`}
        >
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">{items}</DropdownMenuContent>
    </DropdownMenu>
  );
}
